import { Component, EventEmitter, Input, Output } from '@angular/core';
import { faLock, faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import { ChatMiDefaultField } from '@sabstravtech/obtservices/base';

/** One product's worth of recap rows, split into the Per Booking / Per
 * Passenger groups the spec asks for. */
export interface ChatMiRecapGroup {
  basketItemId?: string;
  productName?: string;
  perBooking: ChatMiDefaultField[];
  perPassenger: ChatMiDefaultField[];
}

/**
 * ENT-16431 — MI (cost centre) recap. Lists the collected values with a final
 * Confirm. Per spec, each locked/defaulted row shows a small static padlock
 * on the right and no edit icon — those values were already surfaced as plain
 * description text before the questions started (see `lockedDefaults` on
 * `ChatMiPayload`), so the recap doesn't repeat the tooltip here. Every
 * editable row instead gets a small edit icon that emits `editField` with the
 * field's id, letting the user jump back and re-answer just that one field.
 *
 * Renders the label/value directly (not via app-chat-mi-field-label, which
 * carries the interactive tooltip) — the recap is a compact summary, not the
 * user's first look at these values.
 *
 * Rows are grouped by `basketItemId` under a per-product header (important
 * for multi-product baskets), and within each product split into Per Booking
 * (`PER_BASKET_ITEM`) / Per Passenger (`PER_PASSENGER`) sections per `scope`.
 * Fields missing `basketItemId`/`scope` (legacy server payloads) fall into a
 * single unheaded group with all rows treated as Per Booking, matching the
 * old flat-list rendering.
 */
@Component({
  selector: 'app-chat-mi-recap',
  standalone: false,
  templateUrl: './chat-mi-recap.component.html',
  styleUrls: ['./chat-mi-recap.component.scss']
})
export class ChatMiRecapComponent {
  @Input() fields: ChatMiDefaultField[] = [];
  @Output() confirm = new EventEmitter<void>();
  /** Emits the `ChatMiDefaultField.id` of the row whose edit icon was clicked. */
  @Output() editField = new EventEmitter<string>();

  faLock = faLock;
  faPenToSquare = faPenToSquare;

  get groups(): ChatMiRecapGroup[] {
    const groups: ChatMiRecapGroup[] = [];
    const groupsByKey = new Map<string, ChatMiRecapGroup>();

    for (const field of this.fields) {
      const key = field.basketItemId ?? '';
      let group = groupsByKey.get(key);
      if (!group) {
        group = {
          basketItemId: field.basketItemId,
          productName: field.productName,
          perBooking: [],
          perPassenger: []
        };
        groupsByKey.set(key, group);
        groups.push(group);
      }
      if (field.scope === 'PER_PASSENGER') {
        group.perPassenger.push(field);
      } else {
        group.perBooking.push(field);
      }
    }

    return groups;
  }

  onContinue(): void {
    this.confirm.emit();
  }

  onEdit(field: ChatMiDefaultField): void {
    if (!field.locked && field.id) {
      this.editField.emit(field.id);
    }
  }
}
